import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { userAPI } from '../../api';
import { useAuth } from '../../contexts/AuthContext';

const PointsContainer = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 30px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  margin-bottom: 20px;
  color: #333;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
`;

const PointSummary = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
`;

const PointBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #ef4444;
  color: white;
  border-radius: 9999px;
  width: 80px;
  height: 80px;
  font-size: 1.5rem;
  font-weight: bold;
  margin-right: 20px;
`;

const PointInfo = styled.div`
  flex: 1;
`;

const PointValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #333;
`;

const PointText = styled.div`
  font-size: 14px;
  color: #666;
  margin-top: 5px;
`;

const PointHistoryList = styled.div`
  margin-top: 20px;
`;

const HistoryItem = styled.div`
  padding: 15px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:last-child {
    border-bottom: none;
  }
`;

const HistoryInfo = styled.div`
  flex: 1;
`;

const HistoryTitle = styled.div`
  font-size: 16px;
  color: #333;
  margin-bottom: 5px;
`;

const HistoryDate = styled.div`
  font-size: 12px;
  color: #666;
`;

interface AmountProps {
  positive: boolean;
}

const HistoryAmount = styled.div<AmountProps>`
  font-weight: bold;
  color: ${props => props.positive ? '#10b981' : '#ef4444'};
  font-size: 16px;
  display: flex;
  align-items: center;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

interface PageButtonProps {
  isActive: boolean;
}

const PageButton = styled.button<PageButtonProps>`
  padding: 5px 10px;
  margin: 0 5px;
  background-color: ${props => props.isActive ? '#ef4444' : '#f5f5f5'};
  color: ${props => props.isActive ? 'white' : '#333'};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.isActive ? '#dc2626' : '#e0e0e0'};
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const NoHistory = styled.div`
  text-align: center;
  padding: 30px;
  color: #666;
`;

// 포인트 내역 타입 정의
interface PointHistoryItem {
  log_id: number;
  user_id: number;
  point_change: number;
  exp_change: number;
  reason: string;
  reference_id: number | null;
  reference_type: string | null;
  created_at: string;
  username: string;
}

// 포인트 활동 타입별 설명
const referenceTypeMap: {[key: string]: string} = {
  'post': '게시글 작성',
  'comment': '댓글 작성',
  'login': '로그인',
  'attendance': '출석 체크',
  'admin_manual': '관리자 지급',
  'post_like': '게시글 추천',
  'comment_like': '댓글 추천',
  'level_up': '레벨업 보상',
  'comment_delete': '댓글 삭제'
};

const Points: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pointHistory, setPointHistory] = useState<PointHistoryItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  
  // 포인트 내역 조회
  const fetchPointHistory = async () => {
    setLoading(true);
    try {
      const response = await userAPI.getPointHistory(currentPage);
      const responseData = response.data;
      if (responseData && responseData.success) {
        setPointHistory(responseData.data || []);
        setTotalPages(responseData.pagination?.totalPages || 1);
      } else {
        setError(responseData?.message || '포인트 내역을 불러오는데 실패했습니다.');
      }
    } catch (err: any) {
      setError(err.message || '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };
  
  // 초기 데이터 로드 및 페이지 변경 시 재로드
  useEffect(() => {
    fetchPointHistory();
  }, [currentPage]);
  
  // 페이지 이동
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  
  // 활동 타입 텍스트 변환
  const getReferenceTypeText = (type: string | null) => {
    return type ? (referenceTypeMap[type] || type) : '';
  };
  
  if (loading && pointHistory.length === 0) {
    return <div>로딩 중...</div>;
  }
  
  if (error) {
    return <div>{error}</div>;
  }
  
  return (
    <PointsContainer>
      <SectionTitle>내 포인트</SectionTitle>
      
      <PointSummary>
        <PointBadge>{user?.points ? 'P' : '0P'}</PointBadge>
        <PointInfo>
          <PointValue>{user?.points?.toLocaleString() || 0} 포인트</PointValue>
          <PointText>
            포인트는 게시글 작성, 댓글 작성, 출석체크 등의 활동으로 획득할 수 있습니다.
          </PointText>
        </PointInfo>
      </PointSummary>
      
      <SectionTitle>포인트 내역</SectionTitle>
      
      {pointHistory.length === 0 ? (
        <NoHistory>포인트 내역이 없습니다.</NoHistory>
      ) : (
        <>
          <PointHistoryList>
            {pointHistory.map((item) => (
              <HistoryItem key={item.log_id}>
                <HistoryInfo>
                  <HistoryTitle>
                    {item.reason || getReferenceTypeText(item.reference_type)}
                  </HistoryTitle>
                  <HistoryDate>
                    {new Date(item.created_at).toLocaleString()}
                  </HistoryDate>
                </HistoryInfo>
                <HistoryAmount positive={item.point_change > 0}>
                  {item.point_change > 0 ? '+' : ''}{item.point_change} P
                </HistoryAmount>
              </HistoryItem>
            ))}
          </PointHistoryList>
          
          {totalPages > 1 && (
            <Pagination>
              <PageButton
                disabled={currentPage === 1}
                isActive={false}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                이전
              </PageButton>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <PageButton
                  key={page}
                  isActive={currentPage === page}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </PageButton>
              ))}
              
              <PageButton
                disabled={currentPage === totalPages}
                isActive={false}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                다음
              </PageButton>
            </Pagination>
          )}
        </>
      )}
    </PointsContainer>
  );
};

export default Points; 